// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import {
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from "@tanstack/react-query"
import { apiClient } from "@/queries/client"
import {
  listPollingOptionsFor,
  pollingOptionsFor,
} from "@/lib/refresh-policy"

// Cache key for the `detail` query kind — the single place a cycle's
// `status` lands (ADR-0022). Other query kinds (leaderboard, events,
// snapshot) read it from here via `queryClient.getQueryData` rather than
// re-fetching (PRD §Invariants #3 — TanStack cache is the single source
// of truth for dashboard data).
export const evolutionCycleDetailKey = (cycleId: string | undefined) =>
  ["evolutionCycles", "detail", cycleId] as const

// The latest known status for a cycle, read from the `detail` query's
// cache entry. Every non-detail polling kind feeds this to `intervalFor`.
// `undefined` when the detail query hasn't populated the cache yet —
// treated as non-terminal by `intervalFor`.
export const cachedCycleStatus = (
  queryClient: QueryClient,
  cycleId: string | undefined,
): CycleStatus | undefined =>
  queryClient.getQueryData<EvolutionCycleDetails>(
    evolutionCycleDetailKey(cycleId),
  )?.status

// TanStack hooks for `/front-api/evolution-cycles` (slice 07, M8).
// All three hooks go through the shared `apiClient` (ADR-0011 — single
// instance, interceptor auth, central 401 redirect). Wire is snake_case
// (PRD §Invariants INV-6). Query keys embed params so filtered variants
// coexist in the cache without colliding (ADR-0006 deep-link reload).

export type ListForgeEvolutionCyclesParams = {
  // The four canonical statuses (ADR-0022) — same vocabulary as the
  // global cross-forge listing below. The server treats any unknown
  // value as an empty result (no 400).
  status?: CycleStatus
  started_after?: string
  include_all?: boolean
}

// `status` accepts the four canonical values; an unknown value yields an
// empty list server-side (no 400) — same posture as the per-forge route.
export type ListAllEvolutionCyclesParams = {
  forge_id?: string
  status?: CycleStatus
  started_after?: string
}

const buildAllCyclesParams = (
  params: ListAllEvolutionCyclesParams | undefined,
) => {
  const out: Record<string, string> = {}
  if (params?.forge_id) out.forge_id = params.forge_id
  if (params?.status) out.status = params.status
  if (params?.started_after) out.started_after = params.started_after
  return out
}

export const useAllEvolutionCycles = (
  params?: ListAllEvolutionCyclesParams,
): UseQueryResult<EvolutionCycleGlobalListResponse, Error> => {
  const queryParams = buildAllCyclesParams(params)
  return useQuery<EvolutionCycleGlobalListResponse, Error>({
    queryKey: ["evolutionCycles", "list-all", queryParams],
    staleTime: 1000 * 60,
    // Slice `refresh-policy-live-polling` + DEV-416 follow-up: the
    // `list` kind keys first on the caller's own `?status=` filter —
    // a list scoped to `running` stays live; a list scoped to a
    // terminal status stops. An unfiltered list keys on its rows
    // (function-form `refetchInterval`): it stays live while any row
    // is non-terminal and stops once every row is terminal — nothing
    // in it can still change (`listIntervalFor`).
    ...listPollingOptionsFor(params?.status),
    queryFn: async () => {
      const response =
        await apiClient.get<EvolutionCycleGlobalListResponse>(
          "/front-api/evolution-cycles",
          { params: queryParams },
        )
      return response.data
    },
  })
}

const buildForgeCyclesParams = (
  params: ListForgeEvolutionCyclesParams | undefined,
) => {
  const out: Record<string, string> = {}
  if (params?.status) out.status = params.status
  if (params?.started_after) out.started_after = params.started_after
  if (params?.include_all === true) out.include_all = "true"
  return out
}

// Disabled until `forgeId` is a non-empty string — defensive against
// route params not yet resolved.
export const useForgeEvolutionCycles = (
  forgeId: string | undefined,
  params?: ListForgeEvolutionCyclesParams,
): UseQueryResult<EvolutionCycleListResponse, Error> => {
  const queryParams = buildForgeCyclesParams(params)
  return useQuery<EvolutionCycleListResponse, Error>({
    queryKey: ["evolutionCycles", "list-by-forge", forgeId, queryParams],
    enabled: typeof forgeId === "string" && forgeId.length > 0,
    staleTime: 1000 * 60,
    // Slice `refresh-policy-live-polling` — see the `list`-kind note
    // on `useAllEvolutionCycles` above.
    ...listPollingOptionsFor(params?.status),
    queryFn: async () => {
      const response = await apiClient.get<EvolutionCycleListResponse>(
        `/front-api/forges/${encodeURIComponent(forgeId as string)}/evolution-cycles`,
        { params: queryParams },
      )
      return response.data
    },
  })
}

// Slice `refresh-policy-live-polling`: `refetchInterval` is the `detail`
// kind fed by this query's own cached status (`cachedCycleStatus` against
// `evolutionCycleDetailKey` — the same key this hook writes). The detail
// query is the one place status is fetched, so it never reads another
// query's cache to know when to stop. Terminal status ⇒ `false` (INV-2,
// INV-10); `refetchIntervalInBackground` keeps a hidden tab current (US §3).
export const useEvolutionCycle = (
  cycleId: string | undefined,
): UseQueryResult<EvolutionCycleDetails, Error> => {
  const queryClient = useQueryClient()
  return useQuery<EvolutionCycleDetails, Error>({
    queryKey: evolutionCycleDetailKey(cycleId),
    enabled: typeof cycleId === "string" && cycleId.length > 0,
    // Slice 07: drop the prior 60s `staleTime` so US §11 ("fresh data
    // on tab revisit") actually fires — TanStack Query's
    // `refetchOnWindowFocus` is a no-op while data is still fresh, so
    // keeping the freshness gate ON would defeat the dashboard's hot
    // refresh contract. The cumulative-snapshot cost is bounded
    // (single indexed query in slice 05), so background refetches are
    // cheap.
    refetchOnWindowFocus: true,
    ...pollingOptionsFor("detail", cachedCycleStatus(queryClient, cycleId)),
    queryFn: async () => {
      const response = await apiClient.get<EvolutionCycleDetails>(
        `/front-api/evolution-cycles/${encodeURIComponent(cycleId as string)}`,
      )
      return response.data
    },
  })
}

export type ListEvolutionCycleAgentsParams = {
  role?: "evolver" | "meta" | "domain"
  sort?: "fitness_desc"
  include_all?: boolean
}

const buildAgentsParams = (
  params: ListEvolutionCycleAgentsParams | undefined,
) => {
  const out: Record<string, string> = {}
  if (params?.role) out.role = params.role
  if (params?.sort) out.sort = params.sort
  if (params?.include_all === true) out.include_all = "true"
  return out
}

// Slice `refresh-policy-live-polling`: the `leaderboard` kind reads its
// polling status from the `detail` query's cache entry (this hook never
// fetches status itself) — see `evolutionCycleDetailKey`.
export const useEvolutionCycleAgents = (
  cycleId: string | undefined,
  params?: ListEvolutionCycleAgentsParams,
): UseQueryResult<AgentLeaderboardResponse, Error> => {
  const queryClient = useQueryClient()
  const queryParams = buildAgentsParams(params)
  return useQuery<AgentLeaderboardResponse, Error>({
    queryKey: ["evolutionCycles", "agents", cycleId, queryParams],
    enabled: typeof cycleId === "string" && cycleId.length > 0,
    staleTime: 1000 * 60,
    ...pollingOptionsFor("leaderboard", cachedCycleStatus(queryClient, cycleId)),
    queryFn: async () => {
      const response = await apiClient.get<AgentLeaderboardResponse>(
        `/front-api/evolution-cycles/${encodeURIComponent(cycleId as string)}/agents`,
        { params: queryParams },
      )
      return response.data
    },
  })
}
